import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError } from "@/lib/api";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { Switch } from "@/components/ui/switch";
import { loadRazorpay } from "@/lib/razorpay";
import { Bike, IndianRupee, CheckCircle2, MapPin, Phone, KeyRound, Navigation, Package, ShieldCheck, Upload, Loader2 } from "lucide-react";
import { EmptyState, Spinner } from "@/components/States";
import { toast } from "sonner";

const DOCS = [
  { k: "id_proof_url", label: "Identity Proof (Aadhaar / Voter ID)" },
  { k: "dl_url", label: "Driving License" },
  { k: "pan_url", label: "PAN Card" },
];

function KycGate({ dash, refetch }) {
  const status = dash.verification_status;
  const submitted = dash.kyc_submitted;
  const [kyc, setKyc] = useState({ id_proof_type: "aadhar", id_proof_url: "", dl_url: "", pan_url: "", bank_account: "", bank_ifsc: "", bank_holder: "" });
  const [uploading, setUploading] = useState("");

  const upload = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please upload an image of the document");
    const fd = new FormData(); fd.append("file", file);
    setUploading(key);
    try { const { data } = await api.post("/upload/image", fd); setKyc((p) => ({ ...p, [key]: `${process.env.REACT_APP_BACKEND_URL}${data.url}` })); toast.success("Uploaded"); }
    catch (e) { toast.error(apiError(e)); }
    setUploading("");
  };

  const submit = async () => {
    try { await api.post("/delivery/kyc", kyc); refetch(); toast.success("KYC submitted. Now pay ₹2 to verify your account."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const payVerify = async () => {
    try {
      const { data: init } = await api.post("/delivery/verify/create");
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: init.key_id, amount: init.amount, currency: "INR", order_id: init.rzp_order_id,
        name: "Grofkart", description: `Delivery account verification ₹${init.fee}`, theme: { color: "#F59E0B" },
        handler: async (resp) => { try { await api.post("/delivery/verify/pay", resp); refetch(); toast.success("Account verified! You can now go online."); } catch (e) { toast.error(apiError(e)); } },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (status === "verified") return null;

  return (
    <div data-testid="delivery-kyc-gate" className="mb-6 rounded-2xl border border-amber-300/60 bg-white p-5">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-soft"><ShieldCheck className="h-5 w-5 text-amber-warm" /></span>
        <div>
          <p className="font-head text-lg font-black text-slate-900">Complete your onboarding</p>
          <p className="text-sm text-slate-500">Upload your documents and pay a one-time ₹2 verification to start accepting deliveries.</p>
        </div>
      </div>

      {!submitted ? (
        <div className="space-y-3">
          {DOCS.map((d) => (
            <div key={d.k} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3">
              <span className="text-sm font-semibold text-slate-700">{d.label}</span>
              {kyc[d.k] ? <span className="inline-flex items-center gap-1 text-xs font-bold text-brand"><CheckCircle2 className="h-4 w-4" /> Uploaded</span> : (
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
                  {uploading === d.k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                  <input type="file" accept="image/*" className="hidden" data-testid={`delivery-kyc-${d.k}`} onChange={(e) => upload(d.k, e.target.files?.[0])} />
                </label>
              )}
            </div>
          ))}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <input placeholder="Bank account number" value={kyc.bank_account} onChange={(e) => setKyc({ ...kyc, bank_account: e.target.value })} data-testid="delivery-kyc-bank-account" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="IFSC code" value={kyc.bank_ifsc} onChange={(e) => setKyc({ ...kyc, bank_ifsc: e.target.value.toUpperCase() })} data-testid="delivery-kyc-bank-ifsc" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input placeholder="Account holder name" value={kyc.bank_holder} onChange={(e) => setKyc({ ...kyc, bank_holder: e.target.value })} data-testid="delivery-kyc-bank-holder" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <button onClick={submit} data-testid="delivery-kyc-submit-btn" className="rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white">Submit KYC</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-soft/60 p-4">
          <p className="text-sm font-semibold text-amber-warm">✓ Documents submitted. Pay ₹2 to authenticate your bank mandate and activate your account.</p>
          <button onClick={payVerify} data-testid="delivery-verify-pay-btn" className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white">Pay ₹2 & Verify</button>
        </div>
      )}
    </div>
  );
}

export default function DeliveryDashboard() {
  const qc = useQueryClient();
  const { data: dash, refetch } = useQuery({ queryKey: ["d-dash"], queryFn: () => api.get("/delivery/dashboard").then((r) => r.data), refetchInterval: 5000 });
  const [otp, setOtp] = useState({});

  const toggle = async () => { try { const { data } = await api.patch("/delivery/toggle"); qc.invalidateQueries({ queryKey: ["d-dash"] }); toast.success(data.is_online ? "You're online" : "You're offline"); } catch (e) { toast.error(apiError(e)); } };
  const update = async (id, status) => { try { await api.patch(`/delivery/orders/${id}/status`, { status }); qc.invalidateQueries({ queryKey: ["d-dash"] }); toast.success(status.replace(/_/g, " ")); } catch (e) { toast.error(apiError(e)); } };
  const complete = async (id) => { try { await api.post(`/delivery/orders/${id}/complete`, { otp: otp[id] || "" }); qc.invalidateQueries({ queryKey: ["d-dash"] }); toast.success("Delivered! Earnings updated."); } catch (e) { toast.error(apiError(e)); } };

  if (!dash) return <DashboardShell title="Delivery Partner" roleColor="#F59E0B" roleLabel="Delivery"><div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div></DashboardShell>;

  return (
    <DashboardShell title="Delivery Partner" roleColor="#F59E0B" roleLabel="Delivery"
      headerRight={
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5">
          <span className={`text-xs font-bold ${dash.is_online ? "text-brand" : "text-slate-400"}`}>{dash.is_online ? "Online" : "Offline"}</span>
          <Switch data-testid="delivery-online-toggle" checked={dash.is_online} disabled={dash.verification_status !== "verified"} onCheckedChange={toggle} />
        </div>
      }>
      <KycGate dash={dash} refetch={refetch} />
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Earnings" value={rupee(dash.earnings)} icon={IndianRupee} color="#059669" />
        <StatCard label="Active Deliveries" value={dash.active.length} icon={Bike} color="#F59E0B" />
        <StatCard label="Completed" value={dash.completed_count} icon={CheckCircle2} color="#3B82F6" />
        <StatCard label="Status" value={dash.verification_status === "verified" ? (dash.is_online ? "Online" : "Offline") : "Unverified"} icon={Navigation} color="#8B5CF6" />
      </div>

      <h2 className="mb-3 font-head text-lg font-bold text-slate-900">Active deliveries</h2>
      {dash.active.length === 0 ? (
        <EmptyState icon={Package} title="No active deliveries" subtitle={dash.is_online ? "Waiting for new assignments..." : "Go online to receive orders."} />
      ) : (
        <div className="space-y-4">
          {dash.active.map((o) => (
            <div key={o.id} data-testid="delivery-assigned-order-card" className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="font-head font-bold text-slate-900">#{o.order_no}</p>
                <span className="rounded-md bg-amber-soft px-2 py-0.5 text-[11px] font-bold capitalize text-amber-warm">{o.status.replace(/_/g, " ")}</span>
              </div>
              <div className="mt-2 space-y-1.5 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Package className="h-4 w-4 text-brand" /> Pickup: {o.store_name}</p>
                <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {o.address?.line}, {o.address?.city} {o.address?.pincode}</p>
                <p className="flex items-center gap-2"><Bike className="h-4 w-4 text-brand" /> {o.customer_name} · {rupee(o.total)} · {o.payment_method.toUpperCase()}</p>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a href={`tel:${o.customer_phone}`} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600"><Phone className="h-4 w-4" /> Call</a>
                <button data-testid="delivery-navigate-store-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600"><Navigation className="h-4 w-4" /> Navigate</button>
                {o.status === "assigned" && <button data-testid="delivery-accept-job-btn" onClick={() => update(o.id, "picked_up")} className="rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white">Mark Picked Up</button>}
                {o.status === "picked_up" && <button onClick={() => update(o.id, "out_for_delivery")} className="rounded-xl bg-amber-surge px-4 py-2 text-sm font-bold text-white">Out for Delivery</button>}
                {o.status === "out_for_delivery" && (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <KeyRound className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input data-testid="delivery-enter-otp-input" value={otp[o.id] || ""} onChange={(e) => setOtp({ ...otp, [o.id]: e.target.value })} placeholder="OTP" maxLength={4} className="w-24 rounded-xl border border-slate-200 py-2 pl-8 pr-2 text-sm outline-none focus:border-brand" />
                    </div>
                    <button data-testid="delivery-complete-order-btn" onClick={() => complete(o.id)} className="rounded-xl bg-brand px-4 py-2 text-sm font-bold text-white">Complete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
